import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useAdHub from "../hooks/useAdHub";
import autoAds from "../utils/autoAds";
import isProUser from "../utils/isProUser";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";

/**
 * Automatic rewarded ads on every signed-in page.
 *
 * Pacing (PiNetworkAdsManager.getOptimalInterval): nothing for the first 45 s
 * of a session, then every 90 s, 75 s after 3 min, 60 s after 10 min.
 * Each ad goes through the Ad Hub, so it pays the ATH ladder and charges Turbo
 * like any other ad. It never fires:
 *  - for Pro members,
 *  - while a game round, a Pi payment, or another ad is in progress (autoAds),
 *  - in development (ads only exist in the Pi Browser).
 */
const AutoAdScheduler = () => {
  const userData = useSelector((state: any) => state?.actualUser);
  const { watchAd } = useAdHub();

  const watchAdRef = useRef(watchAd);
  watchAdRef.current = watchAd;
  const isProRef = useRef(false);
  isProRef.current = isProUser(userData?.user);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;
    piNetworkAdsManager.resetSession();
    let timer: NodeJS.Timeout | null = null;

    const tick = () => {
      if (isProRef.current) {
        timer = setTimeout(tick, 60 * 1000);
        return;
      }
      const interval = piNetworkAdsManager.getOptimalInterval();
      if (interval === Infinity) {
        timer = setTimeout(tick, 30 * 1000);
        return;
      }
      const last = Number(localStorage.getItem(autoAds.STORAGE_KEY) || 0);
      const now = Date.now();
      const due = now - last >= interval;
      const blocked = autoAds.isPaused() || piNetworkAdsManager.adInFlight;

      if (due && !blocked) {
        localStorage.setItem(autoAds.STORAGE_KEY, now.toString());
        watchAdRef.current().catch(() => {});
        timer = setTimeout(tick, interval);
      } else {
        // Blocked or not yet due: check again soon rather than waiting a full interval.
        timer = setTimeout(tick, due ? 10 * 1000 : Math.max(5 * 1000, interval - (now - last)));
      }
    };

    tick();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  return null;
};

export default AutoAdScheduler;
